import type { TimeBounds } from "@/shared/domain/topics/determineTimeBounds";
import type { PlotPanelState } from "@/shared/state/visualization";
import {
  PlotAxisConfig,
  PlotYAxis,
  ViewportSyncMode,
} from "@/shared/state/visualization/schema/v1";
import type { MessagePayload } from "@/shared/webworker";

import { TimeDisplayMode } from "../../../WorkspaceCtx/TimeDisplayMode";
import type { SeriesAlignment } from "../alignment";
import type { HoverDatum, Style } from "../PlotRenderer";
import { ViewportParams } from "../Viewport/viewport";

export enum PlotCommand {
  Dispose = "dispose",
  GetDataUnderCursor = "get-data-under-cursor",
  Init = "init",
  Pan = "pan",
  ApplyViewport = "apply-viewport",
  ResetView = "reset-view",
  Resize = "resize",
  SetState = "set-state",
  SetStyle = "set-style",
  UpdateAxisConfig = "update-axis-config",
  Zoom = "zoom",
}

export interface TimeSpanAnnotation {
  // Used to de-dupe annotations at render time. Should re-use an existing meaningful ID like event_id if possible
  annotationId: string;

  // The start and end time of the annotation, in nanoseconds
  startTime: bigint;
  endTime: bigint;

  style?: {
    // Optional text to display on the annotation
    labelText?: string;

    // Optional color to use for the label and background
    labelColor?: string;
    backgroundColor?: string;
  };
}

export interface PlotInit {
  canvas: OffscreenCanvas;
  devicePixelRatio: number;
  timeBounds: TimeBounds;
}

export type ZoomMode = "x" | "y" | "xy";

export interface ZoomPayload {
  zoomRatioX: number;
  zoomRatioY: number;
  center: { x: number; y: number };
  axis: ZoomMode;
}

export interface PanPayload {
  deltaX: number;
  deltaY: number;
}

// TODO(ENG-1429): support multiple y axes
export type Axis = "x" | PlotYAxis;

export interface AxisConfigPayload {
  axis: Axis;
  config: PlotAxisConfig;
}

export interface ApplyViewportPayload {
  params: ViewportParams;
  syncMode: ViewportSyncMode;
}

interface SetStateCommon {
  state: PlotPanelState["data"];
  timeBounds: TimeBounds;
  timeDisplayMode: TimeDisplayMode;
}

/**
 * The two shapes a panel state change can take, mutually exclusive by
 * construction:
 *
 * - Absolute time: the payload carries `annotations`, the event time spans to
 *   draw (an empty array clears them all).
 * - Aligned: the payload carries `alignment` and no annotations, so the worker
 *   clears any left over from absolute mode. An annotation marks an absolute
 *   time span, which has no single x position when each series plots elapsed
 *   time from its own basis, so an aligned state change cannot express one.
 *   {@link SeriesAlignment} defines each entry and how consumers treat a
 *   series that has none.
 *
 * `timeDisplayMode` is meaningful in both shapes: while aligned, absolute
 * modes render as elapsed and the two elapsed modes keep their formatting.
 */
export type SetStatePayload =
  | (SetStateCommon & {
      annotations: TimeSpanAnnotation[];
      alignment?: undefined;
    })
  | (SetStateCommon & {
      alignment: SeriesAlignment;
      annotations?: undefined;
    });

export interface PlotCommandPayloadMap {
  [PlotCommand.Dispose]: undefined;
  [PlotCommand.GetDataUnderCursor]: { x: number; y: number };
  [PlotCommand.Init]: PlotInit;
  [PlotCommand.Pan]: PanPayload;
  [PlotCommand.ApplyViewport]: ApplyViewportPayload;
  [PlotCommand.ResetView]: undefined;
  [PlotCommand.Resize]: { width: number; height: number };
  [PlotCommand.SetState]: SetStatePayload;
  [PlotCommand.SetStyle]: Style;
  [PlotCommand.UpdateAxisConfig]: AxisConfigPayload;
  [PlotCommand.Zoom]: ZoomPayload;
}

export enum PlotCommandResponse {
  DataUnderCursor = "data-under-cursor",
}

export interface PlotCommandResponsePayloadMap {
  [PlotCommandResponse.DataUnderCursor]: HoverDatum[];
}

export function isDisposeCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.Dispose]>
> {
  return event.data.type === PlotCommand.Dispose;
}

export function isGetDataUnderCursorCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    PlotCommand,
    PlotCommandPayloadMap[PlotCommand.GetDataUnderCursor]
  >
> {
  return event.data.type === PlotCommand.GetDataUnderCursor;
}

export function isInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.Init]>
> {
  return event.data.type === PlotCommand.Init;
}

export function isPanCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.Pan]>
> {
  return event.data.type === PlotCommand.Pan;
}

export function isApplyViewportCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.ApplyViewport]>
> {
  return event.data.type === PlotCommand.ApplyViewport;
}

export function isResetViewCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.ResetView]>
> {
  return event.data.type === PlotCommand.ResetView;
}

export function isResizeCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.Resize]>
> {
  return event.data.type === PlotCommand.Resize;
}

export function isSetStateCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.SetState]>
> {
  return event.data.type === PlotCommand.SetState;
}

export function isSetStyleCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.SetStyle]>
> {
  return event.data.type === PlotCommand.SetStyle;
}

export function isUpdateAxisConfigCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    PlotCommand,
    PlotCommandPayloadMap[PlotCommand.UpdateAxisConfig]
  >
> {
  return event.data.type === PlotCommand.UpdateAxisConfig;
}

export function isZoomCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<PlotCommand, PlotCommandPayloadMap[PlotCommand.Zoom]>
> {
  return event.data.type === PlotCommand.Zoom;
}
